import { gsap } from "gsap";

import { AbstractBitmapText } from "../../abstracts/graphics/AbstractBitmapText";
import { CounterData, ValueObj } from "../../abstracts/types/interfaces";

export class ApocalypseCounter extends AbstractBitmapText {
    protected counterData: CounterData;
    public lastValue: number = 0;
    public valueObj: ValueObj;

    constructor(counterData: CounterData) {
        super({ ...counterData.fontProps, text: `${counterData.initialValue}` });

        const defaultData: CounterData = {
            initialValue: 0,
            fontProps: { text: ``, style: { fontName: ``, fontSize: 12 } },
            prefix: ``,
            suffix: ``,
            counterDuration: 0.3,
            onCounterStart: null,
            onCounterUpdate: null,
            onCounterComplete: null,
            highlightColor: 0xffffff,
            highlightDuration: 0.1,
            onHighlightStart: null,
            onHighlightUpdate: null,
            onHighlightComplete: null,
            name: `counter`,
        };

        const finalData = { ...defaultData, ...counterData };

        this.counterData = finalData;
        this.valueObj = { value: +counterData.initialValue };
        this.lastValue = this.valueObj.value;
        this.name = this.counterData.name!;

        this.init();
    }

    private init() {
        this.updateValue(`${this.valueObj.value}`);
    }

    public getUpdateCounterTextAnimation(
        finalValue: number,
        isInstant: boolean = false,
        enableHighlight: boolean = true,
        customCounterAnimationDuration: number = -1,
        customHighlightDuration: number = -1
    ): gsap.core.Timeline {
        if (finalValue === this.valueObj.value) {
            return gsap.timeline();
        }

        let animationDuration: number =
            customCounterAnimationDuration !== -1
                ? customCounterAnimationDuration
                : this.counterData.counterDuration!;
        animationDuration = isInstant ? 0.1 : animationDuration;

        const winObj: ValueObj = {
            value: this.lastValue,
        };

        this.lastValue = finalValue;

        const timeline = gsap.timeline();

        const updateValueAnimation = gsap.to(winObj, {
            value: finalValue,
            duration: animationDuration,
            onStart: () => {
                winObj.value = this.valueObj.value;

                this.counterData.onCounterStart && this.counterData.onCounterStart();
            },
            onUpdate: () => {
                this.counterData.onCounterUpdate && this.counterData.onCounterUpdate();

                this.updateValue(`${Math.round(winObj.value)}`);
            },
            onComplete: () => {
                this.valueObj.value = finalValue;

                this.counterData.onCounterComplete && this.counterData.onCounterComplete();
            },
        });
        timeline.add(updateValueAnimation, 0);

        if (enableHighlight) {
            const highlightDuration: number =
                customHighlightDuration !== -1
                    ? customHighlightDuration
                    : this.counterData.highlightDuration!;
            const highlightAnimation = gsap.to(this, {
                duration: isInstant ? 0.01 : highlightDuration,
                pixi: {
                    colorize: this.counterData.highlightColor,
                    colorizeAmount: 2,
                    brightness: 2,
                    scale: `+=0.05`,
                    y: `-=1`,
                },
                yoyo: true,
                repeat: 1,
                repeatDelay: 0.1,
                onStart: () => {
                    this.counterData.onHighlightStart && this.counterData.onHighlightStart();
                },
                onUpdate: () => {
                    this.counterData.onHighlightUpdate && this.counterData.onHighlightUpdate();
                },
                onComplete: () => {
                    this.counterData.onHighlightComplete && this.counterData.onHighlightComplete();
                },
            });
            timeline.add(highlightAnimation, `>-=${highlightDuration}`);
        }

        return timeline;
    }

    public updateValue(value: string, cacheAsBitmap: boolean = false): void {
        super.updateValue(
            `${this.counterData.prefix}${value}${this.counterData.suffix}`,
            cacheAsBitmap
        );
    }

    reset() {
        super.reset();

        this.valueObj.value = 0;
        this.lastValue = 0;
    }
}
